import { useState, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { employeeCreateSchema, employeeUpdateSchema } from '../validation/employeeSchema';
import * as officeService from '../../../services/officeService';

const emptyDefaults = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  employeeId: '',
  role: 'project-manager',
  installationTrade: '',
  joiningDate: '',
  workLocation: '',
  loginId: '',
  password: '',
  notes: ''
};

export const useEmployeeForm = ({ isOpen, initialData, onSubmit }) => {
  const [locations, setLocations] = useState([]);
  const isEdit = Boolean(initialData?._id);
  const schema = isEdit ? employeeUpdateSchema : employeeCreateSchema;

  const resolver = useMemo(() => zodResolver(schema), [schema]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    setError,
    getValues
  } = useForm({
    resolver,
    defaultValues: emptyDefaults,
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const role = useWatch({ control, name: 'role' });

  useEffect(() => {
    if (isOpen) {
      officeService
        .getLocations()
        .then((res) => setLocations(res.data))
        .catch((err) => console.error('Failed to load locations', err));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    
    if (initialData?._id) {
      reset({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        phone: initialData.phone || '',
        email: initialData.email || '',
        employeeId: initialData.employeeId || '',
        role: initialData.role || 'project-manager',
        installationTrade: initialData.installationTrade || '',
        joiningDate: initialData.joiningDate
          ? new Date(initialData.joiningDate).toISOString().slice(0, 10)
          : '',
        workLocation: initialData.workLocation || '',
        loginId: initialData.loginId || '',
        password: '',
        notes: initialData.notes || ''
      });
    } else {
      reset({ ...emptyDefaults });
    }
    // We only want to reset when the modal OPENS. 
    // Since AddEmployeeModal has a 'key' prop based on employee ID in TeamsScreen, 
    // this hook instance is fresh for each new employee/create action anyway.
  }, [isOpen, reset, initialData]);

  // The cleanup of installationTrade is handled in handleFormSubmit if the role is not field_officer.
  // This avoids clearing the value during the initial form load in edit mode.

  const handleFormSubmit = (data) => {
    const payload = {
      ...data,
      department: 'Tower',
      reportingManager: ''
    };
    if (payload.role !== 'field_officer') {
      delete payload.installationTrade;
    }
    if (isEdit && (!payload.password || payload.password === '')) {
      delete payload.password;
    }
    // Pass reset and setError to the submission handler for flexibility
    onSubmit(payload, { 
      onSuccess: () => reset(emptyDefaults),
      setError: (field, msg) => setError(field, { type: 'manual', message: msg })
    });
  };

  return {
    register,
    submit: (onInvalid) => handleSubmit(handleFormSubmit, onInvalid),
    errors,
    role,
    locations,
    isEdit,
    setError,
    getValues,
    control
  };
};
